/**
 * The languages the app ships, in the order they're offered in the UI.
 *
 * One list, so the i18n bundle, the settings toggle, the auth-screen switcher
 * and every "which locale do I send to the backend?" call site can't drift.
 * `code` must match both a `locales/<code>/translation.json` folder and the
 * `preferred_language` values the API accepts.
 */
export const APP_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'cs', label: 'Čeština' },
  { code: 'fr', label: 'Français' },
  { code: 'sk', label: 'Slovenčina' },
  { code: 'th', label: 'ไทย' },
] as const

export type AppLanguage = (typeof APP_LANGUAGES)[number]['code']

export const APP_LANGUAGE_CODES: readonly string[] = APP_LANGUAGES.map(l => l.code)

/** Source language — what an untranslated key and an unknown tag fall back to. */
export const FALLBACK_LANGUAGE: AppLanguage = 'en'

/**
 * Narrow a BCP-47 tag (`cs-CZ`, `th`, `en-GB`, `undefined`) to a shipped
 * language, falling back to English. Callers pass `i18n.language` straight in.
 *
 * i18next's own `resolvedLanguage` narrows the same way, but types it as
 * `string | undefined`; this returns `AppLanguage`, so a locale handed to the
 * API or a PDF template is one TypeScript has already checked.
 */
export function resolveLanguage(tag: string | undefined | null): AppLanguage {
  const base = (tag ?? '').split('-')[0]
  return APP_LANGUAGE_CODES.includes(base) ? (base as AppLanguage) : FALLBACK_LANGUAGE
}

/**
 * The BCP-47 tag to hand `Intl` / `toLocaleDateString` for a UI language.
 *
 * Thai's default calendar in `Intl` is the Buddhist Era, so a plain `'th'`
 * renders 2026 as 2569. Every date the platform stores, extracts from a
 * document, or prints into a PDF is Gregorian, so a BE year in the UI would
 * contradict the same date in an exported report — in an evidence context
 * that is a real hazard, not a cosmetic one. We pin Gregorian and keep Thai
 * month names. Change this if Thai users want BE dates throughout, but change
 * the PDF templates with it.
 */
export function intlLocale(tag: string | undefined | null): string {
  const language = resolveLanguage(tag)
  return language === 'th' ? 'th-u-ca-gregory' : language
}
